import type { TextQuestion } from '@shared/domain/form';
import type { LegalReview } from '@shared/domain/review';
import { Button } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { QuestionType, QuestionValidationRules } from '@shared/domain/form';
import { ReviewStep } from '@shared/domain/review';
import { LEGAL_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { QuestionRenderer } from '../QuestionRenderer';
import '../../styles/review-form.scss';

interface LegalReviewProps {
  submitReviewForm?: (
    formFields: LegalReview,
    reviewStep: ReviewStep,
    isDraft?: boolean,
  ) => void;
  editMode?: boolean;
  defaultValues?: Partial<LegalReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, import('../../legal-check/types').LegalCheckFlag[]>;
}

function LegalReviewForm(props: LegalReviewProps) {
  const { submitReviewForm, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showAiQuestion = searchParams.get('showAiQuestion') === 'true';

  const formFieldsConfig = showAiQuestion
    ? [
        ...LEGAL_FORM_FIELDS,
        {
          name: 'aiInvolvementComment',
          type: QuestionType.TEXT,
          validationRules: [QuestionValidationRules.REQUIRED],
          question:
            'Please share any legal concerns over the use of AI and how you addressed this.',
        } as TextQuestion,
      ]
    : LEGAL_FORM_FIELDS;

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<LegalReview>({
    reviewStep: ReviewStep.LEGAL,
    formFields: formFieldsConfig,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    seedLegalCheckFlags,
  });

  return (
    <div className="review-page">
      <div data-testid="legal-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 5 · Legal review</p>
        <h1 className="review-page__title">
          {editMode ? 'Edit the legal review' : 'How did the legal review go?'}
        </h1>
        <p className="review-page__subtitle">
          Stay general — describe topics and tactics ("IP indemnification
          pushback", "offered a higher cyber-cap"), not specific clauses or
          dollar amounts.
        </p>
      </header>

      <form>
        <section className="review-section">
          <QuestionRenderer
            questions={formFieldsConfig}
            formFields={formFields}
            errors={errors}
            handleFormFieldChange={handleFormFieldChange}
            extendFormFields={extendFormFields}
            validateField={validateField}
            setFormFields={setFormFields}
            getLegalCheckBindings={getLegalCheckBindings}
          />
        </section>
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode
            ? `Save ${getReviewStepName(ReviewStep.LEGAL)} changes`
            : 'Continue to Negotiation'}
        </Button>
      </div>
    </div>
  );
}

export default LegalReviewForm;
